<template>

    <div class="mb-4">
        <Table :rows="existingRules" :columns="columns" idKey="rule_number" :searchable=false :loading="loading"
            :pagination="false">
            <template #col-description="{ row }">
                <span v-html="row.description"></span>
            </template>
            <template #actions="{ row }">
                <button class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger" color="#FF0000">delete</i>
                </button>
            </template>
        </Table>
    </div>

    <NotificationRules :initialRules="rules" @save="onSave" @saveAll="onSaveAll" @info="openRuleInfo" :loading="loading" :startRuleNumberFromZero="true" />

    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedInstance?.rulename" @confirm="deleteRule" />

    <RuleInfoModal v-model:show="showRuleInfo" :ruleId="infoRuleId" ruleContext="workgroup" />
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import NotificationRules from "@/components/Rules/Rules.vue";
import RuleInfoModal from "@/components/Rules/RuleInfoModal.vue";
import { getLocationId } from "@/utils/SessionDataHandler";
import Table from "../../../components/Table/Table.vue";
import DeleteModal from '@/components/Modal/ConfirmationModal.vue'

const store = useStore();
const route = useRoute();
const toast = useToast();
const locationId = getLocationId();
const workgroupId = Number(route.params.workgroupid);

const loading = ref(true);
const rules = ref([]);
const existingRules = ref([]);
const workgroupTargets = ref([]);
const showDeleteModal = ref(false);
const selectedInstance = ref(null);
const showRuleInfo = ref(false);
const infoRuleId = ref(null);

function openRuleInfo(ruleId) {
    infoRuleId.value = ruleId;
    showRuleInfo.value = true;
}

const columns = [
    { key: 'rulenumber', label: 'Rule Number' },
    { key: 'rulename', label: 'Title' },
    { key: 'description', label: 'Description' },
];

const workgroupTargetOptions = (targets) => {
    if (!targets || !Array.isArray(targets)) return [];
    // Filter out the current workgroup to avoid self-referencing rules
    return targets
        .filter((w) => w.id !== workgroupId)
        .map((w) => ({ value: w.id, label: w.label || `Workgroup ${w.id}` }));
};

const mapWorkgroupRules = (apiRulesArray, targets) => {
    if (!apiRulesArray || !Array.isArray(apiRulesArray)) {
        return [];
    }
    const wgOptions = workgroupTargetOptions(targets);

    return apiRulesArray.map((rule) => {
        const allInputs = rule.inputs || [];

        // Map input fields from API to component format (include target_workgroup_id and reference_workgroup_id as dropdowns)
        const inputFields = allInputs.map((input) => {
            const isTargetOrRefWorkgroup =
                input.key === "target_workgroup_id" || input.key === "reference_workgroup_id";

            const field = {
                key: input.key,
                type: input.type === "integer" ? "number" : input.type,
                label: input.label,
                modelValue: "",
                width: input.type === "integer" ? "80px" : "auto",
            };

            if (isTargetOrRefWorkgroup) {
                field.type = "select";
                field.options = wgOptions;
                return field;
            }

            // Add suffix for day_span fields
            if (input.key === "day_span" || input.key.includes("day")) {
                field.suffix = "day(s)";
            }

            // Add options for select fields (e.g. gender)
            if (input.options) {
                field.type = "select";
                const isGender = (input.key || "").toLowerCase().includes("gender") || (input.label || "").toLowerCase().includes("gender");
                const toLabel = (val) => {
                    if (!isGender) return val;
                    const v = String(val).toLowerCase().trim();
                    if (v === "m" || v === "male" || v === "1") return "Male";
                    if (v === "f" || v === "female" || v === "0") return "Female";
                    return val;
                };
                field.options = input.options.map(opt => ({ value: opt, label: toLabel(opt) }));
            }

            if (input.min !== undefined) field.min = input.min;
            if (input.max !== undefined) field.max = input.max;

            return field;
        });

        return {
            id: rule.rulenumber,
            action: "",
            descriptionHtml: rule.description || rule.title,
            inputFields: inputFields,
            allInputKeys: allInputs.map((i) => i.key),
        };
    });
};

const buildWorkgroupRule = (uiRule) => {
    const ruleDefinition = rules.value.find((r) => r.id === uiRule.id);
    const allInputKeys = ruleDefinition?.allInputKeys || ruleDefinition?.inputFields?.map((f) => f.key) || [];

    if (!ruleDefinition || allInputKeys.length === 0) {
        return [];
    }

    const inputFieldByKey = Object.fromEntries(
        (ruleDefinition.inputFields || []).map((f) => [f.key, f])
    );

    // Build array in API order; use selected dropdown value for target/reference workgroup
    const ruleinfoarray = allInputKeys.map((fieldKey) => {
        if (fieldKey === "target_workgroup_id" || fieldKey === "reference_workgroup_id") {
            const selected = uiRule.fields?.[fieldKey];
            return selected != null && selected !== "" ? selected : null;
        }
        const inputField = inputFieldByKey[fieldKey];
        const value = uiRule.fields?.[fieldKey] ?? (inputField?.type === "number" ? null : "");
        return value;
    });

    return ruleinfoarray;
};

const openDeleteModal = (row) => {
    selectedInstance.value = row
    showDeleteModal.value = true
}

const deleteRule = async () => {
    try {
        await store.dispatch("WorkgroupPreference/removeWorkgroupPreferenceRules", {
            workgroupid: workgroupId,
            locationid: locationId,
            ruleidsarray: [selectedInstance.value.rule_id],
        });
        toast.success("Rule deleted successfully", { position: "top-right" });
        // Re-fetch preferences after deletion
        await loadWorkgroupPreferences();
    } catch (error) {
        console.error("Failed to delete rule:", error);
        toast.error(`Failed to delete rule: ${error.message}`, { position: "top-right" });
    }
};

const loadWorkgroupPreferences = async () => {
    loading.value = true;
    try {
        const response = await store.dispatch("WorkgroupPreference/fetchWorkgroupPreferences", {
            workgroupid: workgroupId,
        });

        // options.workgroup_targets for target/reference workgroup dropdowns
        workgroupTargets.value = response?.options?.workgroup_targets || [];

        // Map rules from API response (pass workgroup targets for dropdown options)
        rules.value = mapWorkgroupRules(response?.rules || [], workgroupTargets.value);

        // Set existing rules for the table
        existingRules.value = response?.existing_rules || [];
    } catch (error) {
        console.error("Failed to load workgroup preferences:", error);
        toast.error(`Failed to load workgroup preferences: ${error.message}`, { position: "top-right" });
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    await loadWorkgroupPreferences();
});

const onSave = async (payload) => {
    try {
        // Build ruleinfoarray in the correct order based on input fields
        const ruleinfoarray = buildWorkgroupRule(payload);

        await store.dispatch("WorkgroupPreference/saveWorkgroupPreferenceRule", {
            workgroupid: workgroupId,
            locationid: locationId,
            rulenumber: payload.id,
            ruleinfoarray,
        });

        toast.success("Rule saved successfully", { position: "top-right" });
        // Re-fetch preferences after saving
        await loadWorkgroupPreferences();
    } catch (error) {
        console.error("Failed to save rule:", error);
        toast.error(`Failed to save rule: ${error.message}`, { position: "top-right" });
    }
};

const onSaveAll = async (payload) => {
    try {
        for (const rule of payload) {
            // Build ruleinfoarray in the correct order based on input fields
            const ruleinfoarray = buildWorkgroupRule(rule);

            await store.dispatch("WorkgroupPreference/saveWorkgroupPreferenceRule", {
                workgroupid: workgroupId,
                locationid: locationId,
                rulenumber: rule.id,
                ruleinfoarray,
            });
        }

        toast.success("All rules saved successfully", { position: "top-right" });
        // Re-fetch preferences after saving all
        await loadWorkgroupPreferences();
    } catch (error) {
        console.error("Failed to save rules:", error);
        toast.error(`Failed to save rules: ${error.message}`, { position: "top-right" });
    }
};
</script>